/* eslint-disable react/no-multi-comp */
import { Registry, withRegistry } from '@bem-react/di';
// @ts-ignore
import { ANSWER_STATUSES } from '@yandex-int/k-common/constants';
import b from 'b_';
import cx from 'classnames';
import { mapValues } from 'lodash';
import React, { CSSProperties, useLayoutEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaginatorWithDescription } from '@edu-frontend/modules/src/shared/ui/PaginatorWithDescription';

import { LESSON_HEADER_HEIGHT } from '@edu-frontend/modules/src/features/lessonHeaderMobile';
import PaginatorButtonBase, { PaginatorButtonProps } from '../../paginator/__button/paginator__button';
import { Paginator as PaginatorBase } from '../../paginator/paginator';
import { PAGINATOR_BUTTON_VIEW, PAGINATOR_ORIENTATION, PAGINATOR_THEME } from '../../paginator/paginator.constants';
import { cnPaginator } from '../../paginator/utils';
import { getShouldShowCurrentAnswersStatus } from '../../problem/common-utils';
import { useCodingPropsSelector } from '../context';
import { useCodingStore } from '../state';
import { MOBILE_CODE_EDITOR_TABS_HEIGHT } from '../__mobile-code-editor/consts';

import { getDesktopView } from './lib/getDesktopView';
import { getMobileView } from './lib/getMobileView';
import { checkPagesType } from './lib/checkPagesType';

import './coding-problem__input-switch.scss';

interface InputSwitchProps {
    view?: 'desktop' | 'mobile';
    ['aria-label']?: string;
    label: string;
    hidden?: boolean;
}

const MAXIMUM_CAPACITY = 8;
const BUTTON_CAPACITY = 2;

const PaginatorButton = ({ mix, ...props }: PaginatorButtonProps) => {
    return (
        <PaginatorButtonBase
            {...props}
            mix={cx(
                mix,
                b('coding-problem', 'input-switch-button', {
                    view: props.view,
                    selected: props.selected,
                    clickable: props.clickable,
                })
            )}
        />
    );
};

const registry = new Registry({ id: cnPaginator() });
registry.set('PaginatorButton', PaginatorButton);
const Paginator = withRegistry(registry)(PaginatorBase);

const Switch = (props: InputSwitchProps) => {
    const { label, view = 'desktop', hidden } = props;
    const intl = useIntl();

    const markup = useCodingPropsSelector((p) => p.problem.markup);
    const inputs = markup.inputs;

    const selectedTest = useCodingStore((s) => s.selectedTest);
    const setSelectedTest = useCodingStore((s) => s.setSelectedTest);

    const attempt = useCodingPropsSelector((p) => p.attempt);
    const showCorrectAnswers = useCodingPropsSelector((p) => p.showCorrectAnswers);
    const showCurrentAnswersStatus = useCodingPropsSelector(getShouldShowCurrentAnswersStatus);
    const inputStatuses = useMemo(() => {
        if (!showCurrentAnswersStatus) {
            return {};
        }
        return mapValues(attempt?.markers?.result || {}, (inputResult) => {
            return inputResult.answer_status || showCorrectAnswers
                ? ANSWER_STATUSES.CORRECT
                : ANSWER_STATUSES.INCORRECT;
        });
    }, [attempt?.markers?.result, showCorrectAnswers, showCurrentAnswersStatus]);

    const pages = useMemo(
        () =>
            inputs.map((input, index) => {
                if (view === 'desktop') {
                    return {
                        onClick: () => setSelectedTest?.(index),
                        selected: selectedTest === index,
                        view: getDesktopView(inputStatuses?.[index], input.hidden),
                        number: index,
                        text: (index + 1).toString(),
                        disabled: input.hidden,
                    };
                }

                const answerStatus = getMobileView(inputStatuses?.[index]);
                return {
                    accepted: Boolean(answerStatus),
                    number: index + 1,
                    disabled: input.hidden,
                    status: answerStatus,
                    ariaHidden: false,
                };
            }),
        [inputs, view, selectedTest, inputStatuses, setSelectedTest]
    );
    const pagesRef = useRef(pages);
    useLayoutEffect(() => void (pagesRef.current = pages));
    useLayoutEffect(() => {
        const firstIncorrect = pagesRef.current.findIndex(
            ({ view: pageView, status, disabled }) =>
                !disabled && (pageView === PAGINATOR_BUTTON_VIEW.ORANGE || status === 'failure')
        );
        if (firstIncorrect !== -1) {
            setSelectedTest(firstIncorrect);
        } else {
            setSelectedTest(0);
        }
    }, [inputStatuses, setSelectedTest]);

    const showButtons = inputs.length > MAXIMUM_CAPACITY;

    if (hidden) {
        return null;
    }

    return (
        <>
            {view === 'desktop' && checkPagesType(pages) ? (
                <div className="coding-problem__input-switch">
                    <div className="coding-problem__input-switch-title">
                        <FormattedMessage id={label} />
                    </div>
                    <Paginator
                        aria-label={undefined}
                        capacity={showButtons ? MAXIMUM_CAPACITY - BUTTON_CAPACITY : MAXIMUM_CAPACITY}
                        orientation={PAGINATOR_ORIENTATION.HORIZONTAL}
                        pages={pages}
                        showDownButton={showButtons}
                        showUpButton={showButtons}
                        theme={PAGINATOR_THEME.CLEAR}
                    />
                </div>
            ) : (
                <PaginatorWithDescription
                    activePageIndex={selectedTest}
                    ariaLabelForButton={({ page, pageNumber, totalPages, isActive }) =>
                        intl.formatMessage(
                            {
                                id: props['aria-label'],
                            },
                            { pageNumber, totalPages, isActive, disabled: page.disabled, testStatus: page.status }
                        )
                    }
                    className="coding-problem__mobile-tests-paginator"
                    defaultButtonView="secondary"
                    disabledButtonView="ghost"
                    onPageClick={setSelectedTest}
                    pages={pages}
                    style={
                        {
                            ['--mobile-code-editor_header-height']: LESSON_HEADER_HEIGHT,
                            ['--mobile-code-editor_tabs-height']: MOBILE_CODE_EDITOR_TABS_HEIGHT,
                        } as CSSProperties
                    }
                    text={intl.formatMessage({ id: label })}
                />
            )}
        </>
    );
};

export const InputSwitch = React.memo(Switch);
